import { type ReactNode } from 'react'

import { clsx } from 'clsx'

import { OptionsList } from './OptionsList'

import type { SelectOption } from './types'

interface DesktopDropdownProps<T> {
  dropdownRef: React.RefObject<HTMLDivElement>
  dropdownPosition: { top: number; left: number; width: number }
  searchPlaceholder: string
  searchQuery: string
  filteredOptions: SelectOption<T>[]
  value: T | null | undefined
  highlightedIndex: number
  isLoading: boolean
  noOptionsText: string
  virtualized: boolean
  itemHeight: number
  maxVisibleItems: number
  listHeight: number
  totalHeight: number
  startIndex: number
  endIndex: number
  offsetY: number
  resolvedFooterAction?: ReactNode
  searchInputRef: React.Ref<HTMLInputElement>
  renderOption?: (option: SelectOption<T>, isSelected: boolean, isHighlighted: boolean) => ReactNode
  onSearchChange: (value: string) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  onSelect: (option: SelectOption<T>) => void
  onSetHighlightedIndex: (index: number) => void
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void
}

interface SearchHeaderProps {
  searchInputRef: React.Ref<HTMLInputElement>
  searchPlaceholder: string
  searchQuery: string
  onSearchChange: (value: string) => void
  onKeyDown: (e: React.KeyboardEvent) => void
}

function SearchHeader({ searchInputRef, searchPlaceholder, searchQuery, onSearchChange, onKeyDown }: SearchHeaderProps): JSX.Element {
  return (
    <div className="p-2 border-b border-border">
      <input
        ref={searchInputRef}
        className="el-input el-input-sm bg-secondary"
        placeholder={searchPlaceholder}
        type="text"
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        onKeyDown={onKeyDown}
      />
    </div>
  )
}

function DropdownOptionsList<T>(p: DesktopDropdownProps<T>): JSX.Element {
  return (
    <OptionsList
      endIndex={p.endIndex}
      filteredOptions={p.filteredOptions}
      highlightedIndex={p.highlightedIndex}
      isLoading={p.isLoading}
      isMobile={false}
      itemHeight={p.itemHeight}
      listHeight={p.listHeight}
      maxVisibleItems={p.maxVisibleItems}
      noOptionsText={p.noOptionsText}
      offsetY={p.offsetY}
      renderOption={p.renderOption}
      startIndex={p.startIndex}
      totalHeight={p.totalHeight}
      value={p.value}
      virtualized={p.virtualized}
      onScroll={p.onScroll}
      onSelect={p.onSelect}
      onSetHighlightedIndex={p.onSetHighlightedIndex}
    />
  )
}

export function DesktopDropdown<T>(p: DesktopDropdownProps<T>): JSX.Element {
  return (
    <div
      ref={p.dropdownRef}
      className={clsx(
        'fixed z-50',
        'bg-popover text-popover-foreground',
        'border border-border',
        'rounded-lg shadow-lg',
        'animate-scale-in',
        'overflow-hidden',
      )}
      role="listbox"
      style={{ top: p.dropdownPosition.top, left: p.dropdownPosition.left, width: p.dropdownPosition.width }}
    >
      <SearchHeader
        searchInputRef={p.searchInputRef}
        searchPlaceholder={p.searchPlaceholder}
        searchQuery={p.searchQuery}
        onKeyDown={p.onKeyDown}
        onSearchChange={p.onSearchChange}
      />
      <DropdownOptionsList {...p} />
      {p.resolvedFooterAction && (
        <div className="border-t border-border p-2">{p.resolvedFooterAction}</div>
      )}
    </div>
  )
}
